import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogIn } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Auto-accept invitation if present
      if (inviteToken) {
        try {
          await axios.post(`${API}/invitations/accept/token/${inviteToken}`);
          toast.success('Invitation acceptée ! Contrat lié.');
          navigate('/contrats');
        } catch (err) {
          const detail = err.response?.data?.detail || "Erreur lors de l'acceptation";
          toast.error(detail);
          navigate('/accueil');
        }
      } else {
        navigate('/accueil');
      }
    } else if (result.error && result.error.includes('email')) {
      toast.info('Pensez à vérifier votre email pour activer toutes les fonctionnalités', { duration: 5000 });
    }
    
    setLoading(false);
  };
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
      data-testid="login-page"
    >
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-400)' }}>
            <LogIn className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-secondary)' }}>
            Connexion
          </CardTitle>
          <CardDescription>
            Connectez-vous à votre compte Nannyou
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.fr"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
              />
              <div className="text-right mt-1">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-[var(--primary-400)] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              style={{ backgroundColor: 'var(--primary-400)' }}
              data-testid="login-button"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-[var(--text-secondary)]">
            Pas encore de compte ?{' '}
            <Link to="/onboarding" className="text-[var(--primary-400)] hover:underline font-medium" data-testid="register-link">
              Créer un compte
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
